import { REPORT_TYPES, type ReportType } from '@obras/shared';

/**
 * O rascunho do relatório novo, guardado no navegador.
 *
 * Fechar o assistente sem querer — Esc, um clique fora, o "×" — não pode custar
 * o que foi escrito. O que está preenchido fica no localStorage, por órgão,
 * pessoa e obra, e volta quando o "Novo relatório" daquela obra é aberto de
 * novo, inclusive depois de recarregar a página. Os arquivos que já subiram
 * voltam pela referência da mídia; o arquivo em si não cabe no navegador, e o
 * que ainda subia quando a tela fechou precisa ser escolhido de novo.
 */
export interface ArquivoGuardado {
  readonly id: string;
  readonly nome: string;
  readonly tamanho: number;
  readonly mime: string;
  readonly video: boolean;
}

export interface Rascunho {
  readonly versao: 1;
  readonly salvoEm: string;
  readonly tipo: ReportType | null;
  readonly titulo: string;
  readonly descricao: string;
  readonly dataDaVisita: string;
  readonly dataDoAto: string;
  readonly valor: string;
  readonly dias: string;
  readonly indice: number;
  readonly alcancada: number;
  readonly arquivos: readonly ArquivoGuardado[];
}

/** Rascunho mais velho que isso não volta: a obra já andou, e o texto envelheceu com ela. */
const VALIDADE_MS = 30 * 24 * 60 * 60 * 1000;

const ETAPA_MAXIMA = 3;

export const chaveDoRascunho = (organizacao: string, pessoa: string, obra: string): string =>
  `obras.relatorio:${organizacao}:${pessoa}:${obra}`;

const texto = (valor: unknown): string => (typeof valor === 'string' ? valor : '');

const etapa = (valor: unknown): number =>
  typeof valor === 'number' && Number.isInteger(valor)
    ? Math.min(Math.max(valor, 0), ETAPA_MAXIMA)
    : 0;

function arquivoGuardado(valor: unknown): ArquivoGuardado | null {
  if (!valor || typeof valor !== 'object') return null;
  const item = valor as Record<string, unknown>;

  if (typeof item['id'] !== 'string' || typeof item['nome'] !== 'string') return null;

  return {
    id: item['id'],
    nome: item['nome'],
    tamanho: typeof item['tamanho'] === 'number' ? item['tamanho'] : 0,
    mime: texto(item['mime']),
    video: item['video'] === true,
  };
}

/**
 * Lê o que estava guardado, ou `null`. O registro pode ser de outra versão do
 * sistema, estar vencido ou nem ser um objeto: nada disso pode quebrar a tela —
 * e o que não serve mais sai do navegador, em vez de ocupar espaço para sempre.
 */
export function lerRascunho(chave: string): Rascunho | null {
  let guardado: string | null;
  try {
    guardado = window.localStorage.getItem(chave);
  } catch {
    return null;
  }
  if (guardado === null) return null;

  const rascunho = interpretar(guardado);
  if (!rascunho) apagarRascunho(chave);
  return rascunho;
}

function interpretar(guardado: string): Rascunho | null {
  let bruto: unknown;
  try {
    bruto = JSON.parse(guardado);
  } catch {
    return null;
  }

  if (!bruto || typeof bruto !== 'object') return null;
  const r = bruto as Record<string, unknown>;
  if (r['versao'] !== 1) return null;

  const salvoEm = texto(r['salvoEm']);
  const quando = Date.parse(salvoEm);
  if (!Number.isFinite(quando) || Date.now() - quando > VALIDADE_MS) return null;

  const tipo = r['tipo'];
  if (tipo !== null && !REPORT_TYPES.includes(tipo as ReportType)) return null;

  return {
    versao: 1,
    salvoEm,
    tipo: tipo as ReportType | null,
    titulo: texto(r['titulo']),
    descricao: texto(r['descricao']),
    dataDaVisita: texto(r['dataDaVisita']),
    dataDoAto: texto(r['dataDoAto']),
    valor: texto(r['valor']),
    dias: texto(r['dias']),
    indice: etapa(r['indice']),
    alcancada: etapa(r['alcancada']),
    arquivos: Array.isArray(r['arquivos'])
      ? r['arquivos'].flatMap((item) => {
          const arquivo = arquivoGuardado(item);
          return arquivo ? [arquivo] : [];
        })
      : [],
  };
}

/** Navegador sem armazenamento (modo privado, cota cheia) não é erro: o rascunho vale enquanto a tela está aberta. */
export function gravarRascunho(chave: string, rascunho: Rascunho): void {
  try {
    window.localStorage.setItem(chave, JSON.stringify(rascunho));
  } catch {
    // Sem armazenamento, o rascunho vive só em memória.
  }
}

export function apagarRascunho(chave: string): void {
  try {
    window.localStorage.removeItem(chave);
  } catch {
    // Nada a apagar onde não há armazenamento.
  }
}
